<template>
  <v-layout column fill-height>
    <v-flex xs12 style="flex-basis: auto">
      <v-toolbar flat class="transparent">
        <v-toolbar-title> Glossary of Terms</v-toolbar-title>

        <v-spacer></v-spacer>

        <ContentSelectionMenu @content-selected="selectFromContentManager" />
      </v-toolbar>

      <v-divider></v-divider>
    </v-flex>

    <v-flex xs12 v-if="showEmptyPlaceholder">
      <v-layout column fill-height align-content-center justify-center>
        <v-flex xs12 style="flex-basis: auto">
          <img style="width: 100%" :src="require('./placeholder-no-glossary.svg')" alt="no-glossary" />
        </v-flex>
        <v-flex xs12 style="flex-basis: auto">
          <h2 class="text-center py-4">Add a Term to the Glossary from the Content Library</h2>
        </v-flex>
      </v-layout>
    </v-flex>

    <GlossarySearch v-if="showSearchField" v-model="searchTerm" />

    <v-flex xs12 v-if="showGlossaryList" style="position: relative">
      <div class="wrapper-overflow">
        <v-flex xs12 v-if="isSearchingTerms" class="glossary-panel__terms">
          <div v-if="showNoSearchResults" class="text-center">
            <span>No terms found</span>
          </div>

          <p-panel-properties v-else three-line>
            <GlossaryTermItem
              v-for="term in filteredTerms"
              :key="getCustomId(term)"
              :term="term"
              :mode="mode"
              @open="openTermNewWindow(term)"
              @remove="removeTerm(term)"
            />
          </p-panel-properties>
        </v-flex>

        <v-flex v-else xs12 class="glossary-panel__terms">
          <p-panel-properties>
            <GlossaryGroup
              v-if="sortedLocalGlossary.length"
              title="Local Glossary"
              action-id="local-glossary"
              :terms="sortedLocalGlossary"
              :mode="mode"
              can-delete-glossary
              @open="openTermNewWindow"
              @remove="removeTerm"
              @delete-group="removeLocalGlossary"
            />

            <GlossaryGroup
              v-for="glossary in sortedImportedGlossaries"
              :key="getCustomId(glossary)"
              :title="getTitle(glossary) + ' (Imported)'"
              :action-id="getCustomId(glossary)"
              :terms="glossary.terms"
              :mode="mode"
              :allow-remove="false"
              can-delete-glossary
              @open="openTermNewWindow"
              @delete-group="removeGlossary(glossary)"
            />

            <GlossaryGroup
              v-if="isGlossaryFromInteractiveBook && sortedInteractiveTextsGlossary.length"
              title="Glossaries from Interactive texts"
              action-id="interactive-texts-glossary"
              :terms="sortedInteractiveTextsGlossary"
              :mode="mode"
              :allow-remove="false"
              @open="openTermNewWindow"
            />
          </p-panel-properties>
        </v-flex>
      </div>
    </v-flex>
  </v-layout>
</template>

<script>
import GlossaryGroup from "./components/GlossaryGroup.vue"
import GlossaryTermItem from "./components/GlossaryTermItem.vue"
import GlossarySearch from "./components/GlossarySearch.vue"
import ContentSelectionMenu from "./components/ContentSelectionMenu.vue"
import PPanelProperties from "../../lessonPlan/blocks/_components/utils/PPanelProperties.vue"
import PPanelPropertiesGroup from "../../lessonPlan/blocks/_components/utils/PPanelPropertiesGroup.vue"
import PPropertyTile from "../../lessonPlan/blocks/_components/utils/PPropertyTile.vue"
import PServiceContent from "plant-common/src/services/PServiceContent"
import { getType, getTitle, getCustomId, getContentId } from "../../../utils/dbTool"

import { STORE_NOTIFICATIONS_NAME, ACTION_NOTIFICATIONS_WARNING } from "../../../store_constants/notifications"
import { mapActions } from "vuex"
import { Extensions } from "plant-common"
import { RevisionDisplayMode } from "plant-common/src/models/revisionDisplayMode"

export default {
  name: "GlossaryPanel",
  components: { 
    ContentSelectionMenu,
    GlossaryGroup,
    GlossarySearch,
    GlossaryTermItem,
    PPanelProperties,
    PPropertyTile,
    PPanelPropertiesGroup
  },
  data() {
    return {
      searchTerm: "",
      mode: this.$attrs.mode,
      value: this.$attrs.value,
      controller: this.$attrs.controller,
      termType: Extensions.term.name,
      glossaryType: Extensions.glossary.name,
      interactiveBookType: Extensions.interactiveBook.name,
      // filteredTerms: []
    }
  },

  computed: {
    sortedLocalGlossary() {
      const terms = this.getLocalGlossary()
      return this.sortTermsByTitle(terms)
    },
    sortedImportedGlossaries() {
      const glossaries = this.getImportedGlossaries()

      return glossaries.map(glossary => {
        const terms = glossary.body.contents.items || []

        return ({
          ...glossary,
          terms: this.sortTermsByTitle(terms)
        })
      })
    },
    sortedInteractiveTextsGlossary() {
      if (!this.isGlossaryFromInteractiveBook) return []

      const terms = this.getInteractiveTextsGlossariesOfInteractiveBook()
      const uniqueTerms = this.mergeTermsByContentId(terms)
      return this.sortTermsByTitle(uniqueTerms)
    },
    isGlossaryFromInteractiveBook() {
      return this.getType(this.value) === this.interactiveBookType
    },
    isEditMode() {
      return this.mode === RevisionDisplayMode.edit
    },
    isSearchingTerms() {
      return this.searchTerm && this.searchTerm !== ''
    },
    showEmptyPlaceholder() {
      return this.filteredTerms.length === 0 && !this.searchTerm
    },
    showSearchField() {
      return this.filteredTerms.length > 0 || this.searchTerm !== ''
    },
    showGlossaryList() {
      return this.filteredTerms.length > 0
    },
    showNoSearchResults() {
      return this.filteredTerms.length === 0
    },
    filteredTerms() {
      const terms = [
        ...this.sortedLocalGlossary,
        ...this.sortedImportedGlossaries.flatMap(glossary => glossary.terms),
        ...this.sortedInteractiveTextsGlossary
      ]

      if (!this.searchTerm) return terms

      const search = this.searchTerm.trim().toLowerCase()
      const mergedTerms = this.mergeTermsByContentId(terms)

      return this.filterBySearch(mergedTerms, search)
    },
  },

  created() {
    if (!this.value.body.contents.hasOwnProperty("glossary")) this.controller.initGlossary()
    if (!this.value.body.contents.hasOwnProperty("glossaryImported")) this.controller.initGlossaryImported()
  },

  methods: {
    ...mapActions(STORE_NOTIFICATIONS_NAME, {
      warning: ACTION_NOTIFICATIONS_WARNING
    }),

    getCustomId,
    getContentId,
    getTitle,
    getType,
    
    getLocalGlossary() {
      const contents = this.value.body.contents
      return contents.glossary ?? []
    },
    
    getImportedGlossaries() {
      const contents = this.value.body.contents
      return contents.glossaryImported ?? []
    },
    
    getInteractiveTextsGlossariesOfInteractiveBook() {
      const InteractiveBook = this.controller.vm
      if (!InteractiveBook.hasGlossaryFromInteractiveText()) return []

      return InteractiveBook.getGlossariesFromInteractiveTexts()
    },

    mergeTermsByContentId(terms) {
      const existingIds = new Set([])
      const mergedTerms = []

      terms.forEach(term => {
        const id = this.getContentId(term)

        if (!existingIds.has(id)) {
          mergedTerms.push(term)
          existingIds.add(id)
        }
      })

      return mergedTerms
    },

    filterBySearch(terms, search) {
      return terms.filter(term => {
        const termText = this.getTermFrom(term).toLowerCase()
        const acronym = this.getAcronymFrom(term).toLowerCase()

        return termText.includes(search) || acronym.includes(search)
      })
    },

    getTermFrom(term) {
      return term.body.contents.term || ""
    },

    getAcronymFrom(term) {
      return term.body.contents.acronym || ""
    },

     selectFromContentManager(selectedContent) {
      if (this.isGlossary(selectedContent[0])) {
        this.importGlossary(selectedContent[0])
        return
      }

      this.importTerms(selectedContent)
    },

    isGlossary(content) {
      return this.getType(content) === this.glossaryType
    },

    importGlossary(glossary) {
      if (this.existsGlossary(glossary)) {
        this.warning({ message: "Glossary already imported" })
        return
      }

      this.controller.setGlossary(glossary)
    },

    existsGlossary(glossary) {
      const contentId = this.getContentId(glossary)
      return this.controller.getGlossaryById(contentId)
    },

    importTerms(terms) {
      const duplicatedTerms = []

      terms.forEach(term => {
        if (this.existsTerm(term)) {
          duplicatedTerms.push(this.getTermFrom(term))
          return
        }

        this.controller.setTermGlossary(term)
      })

      if (duplicatedTerms.length) {
        this.warning({
          message: `Terms already added to Glossary: ${duplicatedTerms}`
        })
      }
    },

    existsTerm(term) {
      const contentId = this.getContentId(term)
      return this.controller.getTermGlossaryById(contentId)
    },

    removeLocalGlossary() {
      this.controller.deleteLocalGlossary()
    },

    removeGlossary(glossary) {
      const contentId = this.getContentId(glossary)
      this.controller.deleteGlossaryById(contentId)
    },

    removeTerm(term) {
      const contentId = this.getContentId(term)
      this.controller.deleteTermGlossaryById(contentId)
    },

    openTermNewWindow(term) {
      PServiceContent.openContentInNewTab(term)
    },

    sortTermsByTitle(terms) {
      return [...terms].sort((aTerm, bTerm) => {
        const aTermTitle = this.getTitle(aTerm).toLowerCase() || ""
        const bTermTitle = this.getTitle(bTerm).toLowerCase() || ""
        return aTermTitle.localeCompare(bTermTitle)
      })
    }
  }
}
</script>
<style>
.glossary-panel__terms .glossary-panel__term .v-list__tile__sub-title > p:last-child {
  margin-bottom: 0;
}
.wrapper-overflow {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow-y: auto;
}
</style>
